/**
 * mentionInboxModel — the pure half of the mentions list (2026-09-17).
 *
 * Two jobs, both decided here so they can be asserted without a Firestore, a
 * DOM or a network:
 *
 *   1. WHICH ROWS the list shows — the workspace filter and the
 *      `people | all` filter, whose default is people-only because Prest0n
 *      addresses the user in nearly every reply and an unfiltered list is
 *      therefore almost entirely his.
 *
 *   2. WHICH READS fetch the catch-up context — one bounded query PER THREAD,
 *      never one per row. The messages either side of a mention cannot be
 *      stored on the row: at the moment a mention is filed the messages AFTER it
 *      do not exist yet, and a stored "before" goes stale the moment anything is
 *      edited. So the context is read live, and the planner below is what keeps
 *      that from turning a thirty-row list into thirty queries.
 */
import type { ThreadMention } from '../../types/threadMention';

/** Messages shown either side of a mention at rest, and once expanded. */
export const MENTION_CONTEXT_RADIUS_COLLAPSED = 1;
export const MENTION_CONTEXT_RADIUS_EXPANDED = 3;

/** Most rows the list renders. Beyond this the badge reads "N+". */
export const MENTION_LIST_CAP = 50;

/** Which mentions the list is showing. */
export type MentionFilter = 'people' | 'all';

export interface MentionListInput {
  rows: readonly ThreadMention[];
  filter: MentionFilter;
  /**
   * The workspace the chat is currently in. Mentions from another workspace are
   * withheld: `switchThread` refuses a foreign thread and hard-navigates through
   * the sealed workspace transition, which loses the message id on the way, so a
   * row that cannot land on its message is a row that should not offer to.
   */
  accountId: string | null;
  cap?: number;
}

/**
 * The rows to render, newest first. Already-sorted input is assumed (the query
 * orders by `orderTs desc`); this only filters and caps, so a re-render never
 * pays for a re-sort of a list the server already ordered.
 */
export function selectMentions(input: MentionListInput): ThreadMention[] {
  const ws = String(input.accountId || '').trim();
  const cap = input.cap ?? MENTION_LIST_CAP;
  const out: ThreadMention[] = [];
  for (const row of input.rows) {
    if (input.filter === 'people' && row.byPrest0n) continue;
    // A row with no workspace stamp predates nothing — every row this code
    // writes carries one — so an absent value is a malformed row, not an old
    // one, and withholding it is the safe direction.
    if (ws && row.accountId !== ws) continue;
    out.push(row);
    if (out.length >= cap) break;
  }
  return out;
}

/**
 * A row whose thread is closed to the viewer: still SHOWN, never counted, never
 * opened (2026-09-21 owner directive — "greyed out … user can still see the
 * summary").
 *
 * Deliberately NOT a filter in `selectMentions`. The owner's ask is the
 * opposite of hiding: the row stays, legible, and explains itself. So this is a
 * decoration predicate, and it is the ONE denominator every consumer shares —
 * the tab's badge, the four places `MentionsList` reads unread, and the context
 * plan — because a retired row that lost the badge but kept its gold
 * call-to-action border would be exactly the badge/list disagreement
 * useThreadActivity's own comment forbids.
 */
export function isMentionRetired(
  row: Pick<ThreadMention, 'threadId'>,
  closedThreadIds: ReadonlySet<string>,
): boolean {
  const id = String(row?.threadId || '').trim();
  return id.length > 0 && closedThreadIds.has(id);
}

/** One bounded read: every mention this list shows in one thread. */
export interface MentionContextWindow {
  threadId: string;
  /** Inclusive range covering every anchor in this thread, padded by the radius. */
  fromOrderTs: number;
  toOrderTs: number;
  /** The anchors the window was built for — what the rows will slice out of it. */
  anchorIds: string[];
}

/**
 * Group the visible rows by thread into one window each.
 *
 * The padding is in MILLISECONDS around the outermost anchors, not a message
 * count, because `orderTs` is the only thing a range query can be expressed in;
 * the caller slices the exact ±radius out of whatever the window returns. It is
 * deliberately generous — a window that returns too much costs a few documents,
 * a window that returns too little silently shows a mention with no context.
 */
export function planContextWindows(
  rows: readonly ThreadMention[],
  radius: number = MENTION_CONTEXT_RADIUS_COLLAPSED,
): MentionContextWindow[] {
  const byThread = new Map<string, MentionContextWindow>();
  // ~2 minutes per message of padding: wide enough that a slow conversation
  // still yields neighbours, bounded enough that a busy thread's window stays
  // small. The slice afterwards is what actually decides how many are shown.
  const padMs = Math.max(1, radius) * 120_000;
  for (const row of rows) {
    const existing = byThread.get(row.threadId);
    if (!existing) {
      byThread.set(row.threadId, {
        threadId: row.threadId,
        fromOrderTs: row.orderTs - padMs,
        toOrderTs: row.orderTs + padMs,
        anchorIds: [row.id],
      });
      continue;
    }
    existing.fromOrderTs = Math.min(existing.fromOrderTs, row.orderTs - padMs);
    existing.toOrderTs = Math.max(existing.toOrderTs, row.orderTs + padMs);
    existing.anchorIds.push(row.id);
  }
  return [...byThread.values()];
}

/** A neighbouring message, flattened to what a preview row needs. */
export interface MentionContextMessage {
  id: string;
  orderTs: number;
  senderId: string;
  senderName: string;
  text: string;
}

/**
 * The messages either side of one anchor, out of that thread's window.
 *
 * Returns what is actually there: a mention at the very start of a thread has
 * nothing before it, and one that is still the newest message has nothing after
 * it — both are ordinary, and the row renders what exists rather than a gap.
 */
export function sliceContextAround(
  window: readonly MentionContextMessage[],
  anchorId: string,
  radius: number,
): { before: MentionContextMessage[]; after: MentionContextMessage[] } {
  const index = window.findIndex((m) => m.id === anchorId);
  if (index < 0) return { before: [], after: [] };
  return {
    before: window.slice(Math.max(0, index - radius), index),
    after: window.slice(index + 1, index + 1 + radius),
  };
}
